import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import IconButton from '@mui/material/IconButton';
import { GradientTypography } from '../gradients/GradientTypography';
import { GradientIcon } from '../gradients/GradientIcon';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { useDebounce } from '../../hooks/useDebounce';
import { toCamelCase } from '../../util/string';
import { mapNumber } from '../../util/mapNumber';
import { useEffect, useState } from 'react';

type Filter = {
  values: string[];
  enabled: boolean;
};

type FilterDropdownSliderProps = {
  title: string;
  cleared: boolean;
  handleFilterChange: (title: string, filter: Filter) => void;
  formatSymbol: string;
  marks: { value: number; label: string; scaledValue: number }[];
  filterFromQuery?: Filter;
};
export function FilterDropdownSlider({
  marks,
  title,
  cleared,
  handleFilterChange,
  formatSymbol,
  filterFromQuery,
}: FilterDropdownSliderProps) {
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<{
    values: number[];
    enabled: boolean;
  }>({
    values: [0, 100],
    enabled: false,
  });
  useEffect(() => {
    if (!filterFromQuery) {
      return;
    }
    setSelected({
      ...filterFromQuery,
      values: filterFromQuery.values.map(inverseScale),
    });
    setExpanded(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterFromQuery]);
  const debouncedSelected = useDebounce(selected, 500);
  const inverseScale = (value) => {
    const closest = marks.reduce((a, b) => {
      const aDiff = Math.abs(a.scaledValue - value);
      const bDiff = Math.abs(b.scaledValue - value);

      if (aDiff === bDiff) {
        return a.scaledValue > b.scaledValue ? a : b;
      }
      return bDiff < aDiff ? b : a;
    });
    //if closest mark is >value, we need to go backwards
    const indexOfClosest = marks.indexOf(closest);
    if (closest.scaledValue >= value) {
      //need to go backwards
      const nextClosest = marks[indexOfClosest - 1];
      const result = mapNumber(
        value,
        nextClosest.scaledValue,
        closest.scaledValue,
        nextClosest.value,
        closest.value,
      );
      return result;
    }
    //we need to go forwards
    const forwardNextClosest = marks[indexOfClosest + 1];
    const forwardResult = mapNumber(
      value,
      closest.scaledValue,
      forwardNextClosest.scaledValue,
      closest.value,
      forwardNextClosest.value,
    );
    return forwardResult;
  };
  const scale = (value) => {
    const percentPerMark = 100 / (marks.length - 1);
    const previousMarkIndex = Math.floor(value / percentPerMark);
    const previousMark = marks[Number(previousMarkIndex)];
    const remainder = value % percentPerMark;
    if (remainder === 0) {
      return previousMark.scaledValue;
    }
    const nextMark = marks[previousMarkIndex + 1];
    const increment =
      (nextMark.scaledValue - previousMark.scaledValue) / percentPerMark;
    return remainder * increment + previousMark.scaledValue;
  };
  const scaleArr = (arr) => arr.map(scale);
  const format = (value) => {
    if (formatSymbol === '%') {
      return `${value.toFixed(0)}${formatSymbol}`;
    }
    return `${formatSymbol}${value.toFixed(0)}`;
  };
  const formatAndScale = (value) => {
    if (formatSymbol === '%') {
      return `${scale(value)?.toFixed(0)}${formatSymbol}`;
    }
    return `${formatSymbol}${scale(value)?.toFixed(0)}`;
  };
  const handleExpand = () => {
    setExpanded((prev) => !prev);
  };
  const handleChange = (e, value) => {
    setSelected({ values: value as number[], enabled: true });
  };
  const handleClose = (e) => {
    e.stopPropagation();
    setSelected({ values: [0, 100], enabled: false });
    if (expanded) {
      setExpanded(false);
    }
  };
  useEffect(() => {
    handleFilterChange(toCamelCase(title), {
      values: scaleArr(debouncedSelected.values),
      enabled: debouncedSelected.enabled,
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSelected]);
  useEffect(() => {
    if (cleared) {
      setSelected({ values: [0, 100], enabled: false });
    }
  }, [cleared]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box>
        <GradientTypography
          gradientColor="primary.vertical"
          sx={{ textTransform: 'uppercase', mb: 0, ml: 2 }}
          variant="h6"
        >
          {title}
        </GradientTypography>
      </Box>
      <Accordion expanded={expanded} variant="filter">
        <AccordionSummary
          onClick={handleExpand}
          expandIcon={
            <IconButton>
              <GradientIcon
                sx={{ width: 20, height: 20 }}
                IconComponent={ExpandMoreIcon}
              />
            </IconButton>
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {selected.values[1] - selected.values[0] !== 100 ? (
              <Typography>{`${formatAndScale(
                selected.values[0],
              )} - ${formatAndScale(selected.values[1])}`}</Typography>
            ) : (
              <Typography>None selected</Typography>
            )}
          </Box>
          {selected.values.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                pl: 2,
              }}
            >
              {selected.values[1] - selected.values[0] !== 100 ? (
                <IconButton color="error" onClick={handleClose}>
                  <GradientIcon
                    sx={{ width: 20, height: 20 }}
                    IconComponent={CloseIcon}
                    gradientColor="error.vertical"
                  />
                </IconButton>
              ) : (
                ''
              )}
            </Box>
          ) : (
            ''
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ px: 8, py: 4 }}>
          <Slider
            value={selected.values}
            onChange={handleChange}
            marks={marks}
            min={0}
            max={100}
            step={1}
            scale={scale}
            valueLabelDisplay="auto"
            valueLabelFormat={format}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
